import type {
  HistogramBucket,
  DistributionItem,
} from './DistributionSection.types'

/**
 * Process construction year histogram buckets into distribution items with grouped categories
 */
export const processConstructionYearBuckets = (
  buckets: HistogramBucket[]
): DistributionItem[] => {
  const total = buckets.reduce((sum, b) => sum + b.doc_count, 0)
  if (total === 0) return []

  // Define construction year categories
  const categories = [
    { label: '<1950', max: 1950 },
    { label: '1950-1970', min: 1950, max: 1970 },
    { label: '1970-1990', min: 1970, max: 1990 },
    { label: '1990-2000', min: 1990, max: 2000 },
    { label: '2000-2010', min: 2000, max: 2010 },
    { label: '2010-2020', min: 2010, max: 2020 },
    { label: '2020+', min: 2020 },
  ]

  // Initialize counts for each category
  const groupedCounts: Record<string, number> = {}
  categories.forEach((cat) => {
    groupedCounts[cat.label] = 0
  })

  // Group buckets into categories
  buckets.forEach((bucket) => {
    const year = bucket.key
    // Filter out invalid years
    if (year < 1800 || year > 2030) return

    for (const cat of categories) {
      const minVal = cat.min ?? 0
      const maxVal = cat.max ?? Infinity
      if (year >= minVal && year < maxVal) {
        groupedCounts[cat.label] += bucket.doc_count
        break
      }
    }
  })

  return categories
    .filter((cat) => groupedCounts[cat.label] > 0)
    .map((cat) => ({
      label: cat.label,
      value: cat.min ?? 0,
      count: groupedCounts[cat.label],
      percentage: (groupedCounts[cat.label] / total) * 100,
    }))
}

/**
 * Process listing duration histogram buckets into distribution items with grouped categories
 */
export const processListingDurationBuckets = (
  buckets: HistogramBucket[]
): DistributionItem[] => {
  const total = buckets.reduce((sum, b) => sum + b.doc_count, 0)
  if (total === 0) return []

  // Define listing duration categories (in days)
  const categories = [
    { label: '<30', max: 30 },
    { label: '30-60', min: 30, max: 60 },
    { label: '60-90', min: 60, max: 90 },
    { label: '90-120', min: 90, max: 120 },
    { label: '120-180', min: 120, max: 180 },
    { label: '180-270', min: 180, max: 270 },
    { label: '270+', min: 270 },
  ]

  // Initialize counts for each category
  const groupedCounts: Record<string, number> = {}
  categories.forEach((cat) => {
    groupedCounts[cat.label] = 0
  })

  // Group buckets into categories
  buckets.forEach((bucket) => {
    const duration = bucket.key
    for (const cat of categories) {
      const minVal = cat.min ?? 0
      const maxVal = cat.max ?? Infinity
      if (duration >= minVal && duration < maxVal) {
        groupedCounts[cat.label] += bucket.doc_count
        break
      }
    }
  })

  return categories
    .filter((cat) => groupedCounts[cat.label] > 0)
    .map((cat) => ({
      label: cat.label,
      value: cat.min ?? 0,
      count: groupedCounts[cat.label],
      percentage: (groupedCounts[cat.label] / total) * 100,
    }))
}
